import { useState } from "react";
import { Modal } from "../common/Modal";
import { Button } from "../common/Button";
import { useFeatureTheme } from "../../context/FeatureThemeContext";
import { themeApi } from "../../services/userApi";
import { THEME_COPY } from "../../pages/settings/themeCopy";

/**
 * Telling people the presets exist, exactly once per upgrade (#487).
 *
 * V128 shipped four presets and nothing pointed anybody at them. An
 * account on Harvester is on Basic or Advanced and has no reason to open
 * Theme Settings, so the work was invisible until somebody went looking.
 *
 * ── THE SERVER DECIDES WHETHER TO ASK ──
 *
 * `theme_nudge` arrives on the theme payload, already null unless every
 * one of #487's conditions holds. This component renders what it is
 * given and decides nothing: no tier arithmetic, no "have I shown this"
 * bookkeeping, no localStorage.
 *
 * That split is the whole design. #487 originally said to follow
 * `WhatsNewModal`, which I had assumed asked the server -- it does not,
 * it is a per-browser `localStorage` key. A per-browser flag re-prompts
 * on a second device and after a cache clear, and on mobile the theme is
 * hydrated from cache so the flag would be wrong offline in both
 * directions.
 *
 * ── "I'LL DECIDE LATER" IS AN ANSWER, NOT A DISMISSAL ──
 *
 * It posts to the same endpoint a selection does. #487 is explicit that
 * skipping changes nothing -- no theme applied, no feature toggled, no
 * second prompt -- and "no second prompt" is the part that needs a write.
 * A dialog that reappears because somebody closed it politely is the
 * nagging this issue exists to avoid.
 *
 * CLOSING IT COUNTS TOO. Pressing Escape or clicking the backdrop is the
 * same intent as "later", and treating only the button as an answer would
 * make the Escape key the one route that guarantees being asked again.
 */
export function ThemeNudgeModal() {
  const { theme, themes, refresh, themeNudge } = useFeatureTheme();
  const [busy, setBusy] = useState(false);
  /* The tier it was answered FOR, not a flag (#561). A flag stayed true for
     the whole session, so upgrading Free -> Harvester -> Business without a
     reload never showed the second nudge. */
  const [answeredTier, setAnsweredTier] = useState(null);

  const offered = (themeNudge?.themes ?? []).filter((name) => THEME_COPY[name]);
  if (!themeNudge || answeredTier === themeNudge.tier || offered.length === 0) return null;

  /* OPTIMISTIC, and deliberately so. Closing the modal must not wait on a
     round trip, and the write is "we told them" rather than anything the
     user is waiting to see. If it fails they get asked once more on the
     next load, which is a far better failure than a modal that hangs. */
  async function answer(pick) {
    setBusy(true);
    setAnsweredTier(themeNudge.tier);
    try {
      if (pick) await themeApi.update(pick);
      await themeApi.answerNudge();
      await refresh();
    } catch {
      /* Swallowed on purpose: see above. The nudge reappearing once is
         the intended consequence of a failed write, and an error toast
         about a tip nobody asked for is worse than the retry. */
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={() => answer(null)}
      title="New ways to set up what you see"
      footer={
        <Button variant="secondary" disabled={busy} onClick={() => answer(null)}>
          I&rsquo;ll decide later
        </Button>
      }
    >
      <p style={{ marginTop: 0 }}>
        Your plan now includes {offered.length === 1 ? "a theme" : "themes"} built
        around how an operation actually runs, instead of switching features on one
        at a time. You can change or undo this whenever you like &mdash; a theme only
        changes what is in front of you, never what your plan includes.
      </p>

      <div style={{ display: "grid", gap: "var(--space-3)" }}>
        {offered.map((name) => (
          <div key={name} style={{
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-md)",
            padding: "var(--space-3)",
          }}>
            <h3 style={{ marginTop: 0, marginBottom: "var(--space-2)" }}>
              {THEME_COPY[name].title}
            </h3>
            <p style={{ margin: "0 0 var(--space-3)" }}>{THEME_COPY[name].description}</p>
            <Button
              disabled={busy || theme === name}
              onClick={() => answer(name)}
            >
              {theme === name ? "Already using this" : `Use ${THEME_COPY[name].title}`}
            </Button>
          </div>
        ))}
      </div>

      {/* The route stays discoverable after this closes, which is what
          #487 wanted a dashboard task for. Said here because somebody
          choosing "later" has just been told where to go, and a task
          repeating it is a second prompt about the same thing. */}
      <p style={{ color: "var(--color-text-muted)", marginBottom: 0 }}>
        {themes?.length ? "All of them live in " : "These live in "}
        <strong>Settings &rarr; Theme</strong>, with the full list and the
        individual features.
      </p>
    </Modal>
  );
}
